import { Link, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import {
    Download,
    Share2,
    Building2,
    Calendar,
    Coins,
    CheckCircle2,
    AlertTriangle,
    FileText,
    Sparkles,
    Languages,
    Target,
    ArrowRight,
    Hash,
    Loader2,
    Upload,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/lib/supabaseClient";
import { downloadAnalysisPdf } from "@/lib/reportPdf";
import { toast } from "sonner";

const RISK_COLORS = {
    Low: {
        bg: "bg-emerald-50 dark:bg-emerald-950/30",
        border: "border-emerald-200 dark:border-emerald-900/40",
        text: "text-emerald-700 dark:text-emerald-300",
        badge: "bg-emerald-100 text-emerald-700 border-emerald-200",
    },
    Medium: {
        bg: "bg-amber-50 dark:bg-amber-950/30",
        border: "border-amber-200 dark:border-amber-900/40",
        text: "text-amber-700 dark:text-amber-300",
        badge: "bg-amber-100 text-amber-700 border-amber-200",
    },
    High: {
        bg: "bg-red-50 dark:bg-red-950/30",
        border: "border-red-200 dark:border-red-900/40",
        text: "text-red-700 dark:text-red-300",
        badge: "bg-red-100 text-red-700 border-red-200",
    },
};

const STATUS_BADGE = {
    Eligible: "bg-emerald-50 text-emerald-700 hover:bg-emerald-50 border border-emerald-200",
    "Partially Eligible":
        "bg-amber-50 text-amber-700 hover:bg-amber-50 border border-amber-200",
    "Not Eligible": "bg-red-50 text-red-700 hover:bg-red-50 border border-red-200",
};

const RISK_TO_STATUS = {
    Low: "Eligible",
    Medium: "Partially Eligible",
    High: "Not Eligible",
};

async function copyText(text) {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        // legacy fallback for browsers/contexts that block the Clipboard API
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.cssText = "position:fixed;left:-9999px;top:0;";
        document.body.appendChild(ta);
        ta.select();
        let ok = false;
        try {
            ok = document.execCommand("copy");
        } catch {
            ok = false;
        }
        ta.remove();
        return ok;
    }
}

function matchLabel(score) {
    if (score >= 70) return { text: "Strong fit", cls: "text-emerald-600" };
    if (score >= 45) return { text: "Moderate fit", cls: "text-amber-600" };
    return { text: "Weak fit", cls: "text-red-600" };
}

export default function AnalysisResult() {
    const { id } = useParams();
    const [row, setRow] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            const { data } = await supabase
                .from("tender_analyses")
                .select("*")
                .eq("id", id)
                .maybeSingle();
            setRow(data);
            setLoading(false);
        })();
    }, [id]);

    if (loading) return <LoadingState />;

    const a = row?.result;
    if (!row || !a) return <MissingResult row={row} />;

    const score = Math.max(0, Math.min(100, Number(a.match_score) || 0));

    return (
        <div className="space-y-6" data-testid="analysis-result-page">
            <ResultHeader a={a} score={score} />
            <KeyMetrics a={a} />
            <SummaryCards a={a} />
            <div className="grid lg:grid-cols-2 gap-6">
                <DocumentsCard documents={a.documents || []} />
                <RisksCard risks={a.risks || []} />
            </div>
            <NextActionPanel nextAction={a.next_action} />
        </div>
    );
}

function LoadingState() {
    return (
        <div
            className="flex flex-col items-center justify-center py-32 text-slate-500"
            data-testid="analysis-result-loading"
        >
            <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
            <p className="mt-4 text-sm">Loading analysis...</p>
        </div>
    );
}

function MissingResult({ row }) {
    return (
        <div
            className="rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-12 text-center"
            data-testid="analysis-result-empty"
        >
            <FileText className="h-10 w-10 text-slate-400 mx-auto" />
            <h2 className="mt-4 text-xl font-bold text-slate-900 dark:text-slate-50">
                {row ? "No AI report stored for this analysis" : "Analysis not found"}
            </h2>
            <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto">
                {row
                    ? `"${row.file_name}" was analyzed before AI reports were saved. Upload the tender again to generate a full report.`
                    : "This analysis doesn't exist or belongs to another account."}
            </p>
            <Link to="/dashboard/upload" className="mt-6 inline-block">
                <Button className="bg-indigo-600 hover:bg-indigo-700 text-white">
                    <Upload className="h-4 w-4 mr-2" /> Analyze a Tender
                </Button>
            </Link>
        </div>
    );
}

function ResultHeader({ a, score }) {
    const statusCls = STATUS_BADGE[a.eligibility_status] || STATUS_BADGE.Eligible;
    const riskCls =
        STATUS_BADGE[RISK_TO_STATUS[a.risk_level]] || STATUS_BADGE.Eligible;
    const [downloading, setDownloading] = useState(false);

    const handleDownload = async () => {
        setDownloading(true);
        try {
            await downloadAnalysisPdf(a);
            toast.success("PDF report downloaded");
        } catch (err) {
            console.error("PDF generation failed:", err);
            toast.error("Could not generate the PDF. Please try again.");
        } finally {
            setDownloading(false);
        }
    };

    const handleShare = async () => {
        const url = window.location.href;
        if (navigator.share) {
            try {
                await navigator.share({
                    title: `Tender Analysis: ${a.name}`,
                    text: `AI tender analysis of "${a.name}" — Match score ${score}/100, ${a.eligibility_status}.`,
                    url,
                });
                return;
            } catch (err) {
                if (err?.name === "AbortError") return; // user closed the share sheet
            }
        }
        const copied = await copyText(url);
        if (copied) toast.success("Report link copied to clipboard");
        else toast.error("Could not share the report");
    };

    return (
        <div className="rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-7 lg:p-8">
            <div className="flex flex-col lg:flex-row lg:items-start gap-6 lg:justify-between">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-3">
                        <Badge className={statusCls} data-testid="result-eligibility-badge">
                            <CheckCircle2 className="h-3 w-3 mr-1" /> {a.eligibility_status}
                        </Badge>
                        <Badge className={riskCls} data-testid="result-risk-badge">
                            {a.risk_level} Risk
                        </Badge>
                        <span className="text-xs text-slate-500 font-mono inline-flex items-center gap-1">
                            <Hash className="h-3 w-3" />
                            {a.tender_no}
                        </span>
                    </div>
                    <h1
                        className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-50 text-balance"
                        data-testid="result-tender-name"
                    >
                        {a.name}
                    </h1>
                    <p className="mt-2 text-sm text-slate-600 dark:text-slate-400 flex items-center gap-2">
                        <Building2 className="h-3.5 w-3.5" /> {a.department}
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        className="border-slate-200 dark:border-slate-700"
                        onClick={handleShare}
                        data-testid="result-share-btn"
                    >
                        <Share2 className="h-4 w-4 mr-2" /> Share
                    </Button>
                    <Button
                        className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-md shadow-indigo-500/25"
                        onClick={handleDownload}
                        disabled={downloading}
                        data-testid="result-download-btn"
                    >
                        {downloading ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                            <Download className="h-4 w-4 mr-2" />
                        )}
                        {downloading ? "Generating..." : "Download PDF"}
                    </Button>
                </div>
            </div>

            <MatchScorePanel score={score} />
        </div>
    );
}

function MatchScorePanel({ score }) {
    const fit = matchLabel(score);
    return (
        <div className="mt-7 rounded-2xl border border-indigo-200 dark:border-indigo-900/40 bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-950/40 dark:to-violet-950/40 p-5">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-indigo-700 dark:text-indigo-300">
                        <Target className="h-3.5 w-3.5" /> Tender Match Score
                    </div>
                    <p className="mt-2 text-sm text-slate-700 dark:text-slate-300 max-w-md">
                        AI-estimated accessibility based on eligibility strictness, EMD size
                        and document requirements.
                    </p>
                </div>
                <div className="text-right">
                    <p
                        className="text-4xl font-bold font-mono text-indigo-700 dark:text-indigo-300"
                        data-testid="result-match-score"
                    >
                        {score}
                        <span className="text-lg text-indigo-400">/100</span>
                    </p>
                    <p className={`text-xs font-semibold ${fit.cls}`}>{fit.text}</p>
                </div>
            </div>
            <div className="mt-4 h-2 rounded-full bg-white dark:bg-slate-900 overflow-hidden">
                <div
                    className="h-full bg-gradient-to-r from-indigo-500 via-violet-500 to-emerald-500 rounded-full"
                    style={{
                        width: `${score}%`,
                        boxShadow: "0 0 16px rgba(99,102,241,0.5)",
                    }}
                />
            </div>
        </div>
    );
}

function KeyMetrics({ a }) {
    const metrics = [
        { icon: Coins, label: "Tender Value", value: a.value },
        { icon: Coins, label: "EMD Amount", value: a.emd, sub: a.emd_mode },
        {
            icon: Calendar,
            label: "Last Date",
            value: a.deadline_date,
            sub: a.deadline_time,
        },
        { icon: Calendar, label: "Published", value: a.published_date },
    ];
    return (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {metrics.map((m) => (
                <div
                    key={m.label}
                    className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5"
                >
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-800">
                        <m.icon className="h-4 w-4 text-slate-700 dark:text-slate-300" />
                    </div>
                    <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-slate-500">
                        {m.label}
                    </p>
                    <p className="mt-1 text-lg font-bold font-mono text-slate-900 dark:text-slate-50 truncate">
                        {m.value}
                    </p>
                    {m.sub && <p className="text-xs text-slate-500 mt-0.5">{m.sub}</p>}
                </div>
            ))}
        </div>
    );
}

function SummaryCards({ a }) {
    return (
        <div className="grid lg:grid-cols-2 gap-6">
            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
                <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-indigo-600" />
                    <h3 className="font-semibold text-slate-900 dark:text-slate-50">
                        AI Summary
                    </h3>
                </div>
                <p
                    className="mt-4 text-sm leading-relaxed text-slate-700 dark:text-slate-300"
                    data-testid="result-summary"
                >
                    {a.summary}
                </p>
            </div>
            <div className="rounded-2xl border border-violet-200 dark:border-violet-900/40 bg-gradient-to-br from-violet-50/60 to-white dark:from-violet-950/30 dark:to-slate-900 p-6">
                <div className="flex items-center gap-2">
                    <Languages className="h-4 w-4 text-violet-600" />
                    <h3 className="font-semibold text-slate-900 dark:text-slate-50">
                        All Language Summary
                    </h3>
                </div>
                <p className="mt-1 text-xs text-violet-700 dark:text-violet-300 font-medium">
                    {a.language_summary_label}
                </p>
                <p className="mt-4 text-sm leading-relaxed text-slate-700 dark:text-slate-300">
                    {a.language_summary}
                </p>
            </div>
        </div>
    );
}

function DocumentsCard({ documents }) {
    return (
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-indigo-600" />
                    <h3 className="font-semibold text-slate-900 dark:text-slate-50">
                        Required Documents
                    </h3>
                </div>
                <span className="text-xs font-semibold text-slate-500">
                    {documents.length} items
                </span>
            </div>
            <ul className="mt-5 space-y-2.5" data-testid="result-documents-list">
                {documents.map((d, i) => (
                    <li
                        key={d}
                        className="flex items-start gap-3 text-sm text-slate-700 dark:text-slate-300"
                    >
                        <span className="flex h-5 w-5 items-center justify-center rounded-md bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 text-[10px] font-bold font-mono shrink-0 mt-0.5">
                            {String(i + 1).padStart(2, "0")}
                        </span>
                        {d}
                    </li>
                ))}
            </ul>
        </div>
    );
}

function RisksCard({ risks }) {
    return (
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
            <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <h3 className="font-semibold text-slate-900 dark:text-slate-50">
                    Risk Factors
                </h3>
            </div>
            <div className="mt-5 space-y-3" data-testid="result-risks-list">
                {risks.map((r) => {
                    const c = RISK_COLORS[r.level] || RISK_COLORS.Medium;
                    return (
                        <div
                            key={r.title}
                            className={`rounded-xl border ${c.border} ${c.bg} p-4`}
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <h4 className={`font-semibold text-sm ${c.text}`}>
                                        {r.title}
                                    </h4>
                                    <p className="mt-1 text-xs leading-relaxed text-slate-600 dark:text-slate-400">
                                        {r.desc}
                                    </p>
                                </div>
                                <Badge
                                    className={`${c.badge} hover:${c.badge} text-xs shrink-0`}
                                    variant="outline"
                                >
                                    {r.level}
                                </Badge>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function NextActionPanel({ nextAction }) {
    return (
        <div className="rounded-3xl bg-gradient-to-br from-slate-900 to-indigo-900 dark:from-indigo-950 dark:to-slate-950 text-white p-7 lg:p-9 relative overflow-hidden">
            <div className="absolute inset-0 grid-pattern opacity-10" />
            <div className="relative max-w-3xl">
                <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-indigo-300">
                    <Sparkles className="h-3.5 w-3.5" /> Suggested Next Action
                </div>
                <p
                    className="mt-4 text-lg lg:text-xl text-white leading-relaxed"
                    data-testid="result-next-action"
                >
                    {nextAction}
                </p>
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                    <Button className="bg-white text-indigo-700 hover:bg-slate-100 font-semibold">
                        Add to My Bid Plan
                        <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                    <Link to="/dashboard/upload">
                        <Button
                            variant="outline"
                            className="border-white/30 bg-white/10 hover:bg-white/20 text-white font-semibold w-full"
                        >
                            Analyze Another Tender
                        </Button>
                    </Link>
                </div>
            </div>
        </div>
    );
}
